import React, { useEffect, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { CheckCircle2, XCircle, Loader2, BellOff } from "lucide-react";
import { Helmet } from "react-helmet-async";
import api from "@/lib/api";

/**
 * Public one-click unsubscribe landing page — hit from the "Unsubscribe" link
 * in weekly Job Alert digests. No auth required (token is the auth).
 */
const Unsubscribe = () => {
    const [params] = useSearchParams();
    const token = params.get("t");
    const [state, setState] = useState({ loading: true, ok: false, message: "" });

    useEffect(() => {
        if (!token) {
            setState({ loading: false, ok: false, message: "Missing unsubscribe token." });
            return;
        }
        api.get(`/unsubscribe/job-alerts/${token}`)
            .then(({ data }) => setState({ loading: false, ok: true, message: data.message }))
            .catch((err) => setState({
                loading: false, ok: false,
                message: err?.response?.data?.detail || "This link is invalid or already used.",
            }));
    }, [token]);

    return (
        <div data-testid="unsubscribe-page" className="min-h-[60vh] flex items-center justify-center px-6 py-16">
            <Helmet><title>Unsubscribe · Simonara Job Alerts</title></Helmet>
            <div className="max-w-md w-full text-center">
                <div className="mx-auto mb-5 flex h-14 w-14 items-center justify-center rounded-full bg-emerald-50 ring-1 ring-emerald-200 dark:bg-emerald-500/10 dark:ring-emerald-500/30">
                    {state.loading
                        ? <Loader2 className="h-6 w-6 animate-spin text-emerald-600 dark:text-emerald-400" />
                        : state.ok
                            ? <CheckCircle2 className="h-6 w-6 text-emerald-600 dark:text-emerald-400" />
                            : <XCircle className="h-6 w-6 text-rose-600 dark:text-rose-400" />}
                </div>
                <h1 className="font-heading font-semibold text-2xl text-foreground">
                    {state.loading ? "Unsubscribing…" : state.ok ? "You're unsubscribed" : "Couldn't unsubscribe"}
                </h1>
                <p className="mt-3 text-sm text-muted-foreground leading-relaxed" data-testid="unsub-message">
                    {state.message}
                </p>
                {state.ok && (
                    <div className="mt-6 text-xs text-muted-foreground">
                        <BellOff size={12} className="inline mr-1" /> You can still sign in and create new alerts anytime.
                    </div>
                )}
                <div className="mt-8 flex justify-center gap-3">
                    <Link to="/careers" className="btn-outline text-sm">Browse open roles</Link>
                    <Link to="/professional/job-alerts" className="btn-green text-sm">Manage alerts</Link>
                </div>
            </div>
        </div>
    );
};

export default Unsubscribe;
